import React, { useState, useEffect } from "react";
import {
  Clock,
  Loader2,
  CheckCircle,
  AlertCircle,
  Calendar,
  X,
  User,
  Mail,
  Phone,
  MessageSquare,
  CreditCard,
  Landmark,
  Flame,
  HeartHandshake,
  ScrollText,
  Sparkles,
  BellRing,
} from "lucide-react";

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState(0);

  const [bookingForm, setBookingForm] = useState({
    service: "",
    name: "",
    email: "",
    phone: "",
    bookingDate: "",
    bookingTime: "",
    specialRequirements: "",
    amount: 0,
  });

  const parseAmount = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatSelectedAmount = (value) => parseAmount(value).toFixed(2);

  // Razorpay Configuration
  const RAZORPAY_KEY_ID = "rzp_live_RcOu7sPz4MvGiH";
  const TEMPLE_NAME = "Sri Kalikamundeshwari Temple";

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (bookingForm.bookingDate && selectedService) {
      fetchAvailableSlots(bookingForm.bookingDate);
    }
  }, [bookingForm.bookingDate, selectedService]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        "https://srikalikamundeshwari.com/api/services",
      );
      const result = await response.json();
      if (result.success) {
        setServices(result.data);
      } else {
        setError(result.message || "Failed to load services");
      }
    } catch (err) {
      setError("Unable to connect to server");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const response = await fetch(
        `https://srikalikamundeshwari.com/api/available-slots/${date}`,
      );
      const result = await response.json();
      if (result.success) {
        setAvailableSlots(result.data);
      }
    } catch (error) {
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleBookNow = (service) => {
    const amount = parseAmount(service.price_min);
    setSelectedService(service);
    setSelectedAmount(amount);
    setBookingForm((prev) => ({
      ...prev,
      service: service.name,
      amount,
    }));
    setShowBookingModal(true);
    setSubmitStatus(null);
  };

  const handleCloseModal = () => {
    setShowBookingModal(false);
    setSelectedService(null);
    setBookingForm({
      service: "",
      name: "",
      email: "",
      phone: "",
      bookingDate: "",
      bookingTime: "",
      specialRequirements: "",
      amount: 0,
    });
    setAvailableSlots([]);
    setSubmitStatus(null);
    setSelectedAmount(0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBookingForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAmountChange = (amount) => {
    const parsedAmount = parseAmount(amount);
    setSelectedAmount(parsedAmount);
    setBookingForm((prev) => ({
      ...prev,
      amount: parsedAmount,
    }));
  };

  const getAmountOptions = (service) => {
    if (service.price_min === 0 && service.price_max === 0) {
      return [{ value: 0, label: "Free Service" }];
    }
    if (service.price_min === service.price_max) {
      return [{ value: service.price_min, label: `₹${service.price_min}` }];
    }

    const options = [];
    const step = Math.max(
      Math.floor((service.price_max - service.price_min) / 4),
      100,
    );

    for (
      let amount = service.price_min;
      amount <= service.price_max;
      amount += step
    ) {
      options.push({ value: amount, label: `₹${amount}` });
    }

    if (options[options.length - 1].value < service.price_max) {
      options.push({
        value: service.price_max,
        label: `₹${service.price_max}`,
      });
    }

    return options;
  };

  const initializeRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    // Validate form
    const requiredFields = ["name", "email", "phone", "bookingDate"];
    const missingFields = requiredFields.filter((field) => !bookingForm[field]);

    if (missingFields.length > 0) {
      alert(`Please fill in: ${missingFields.join(", ")}`);
      return;
    }

    setIsSubmitting(true);

    try {
      // If free service, directly create booking
      if (selectedAmount === 0) {
        await createBooking(null, null, null);
        return;
      }

      console.log("🔍 Starting payment process...");
      console.log("📊 Amount:", selectedAmount);

      // Initialize Razorpay
      const res = await initializeRazorpay();
      if (!res) {
        alert(
          "Razorpay SDK failed to load. Please check your internet connection.",
        );
        setIsSubmitting(false);
        return;
      }
      console.log("✅ Razorpay SDK loaded");

      // Create Razorpay order
      console.log("📤 Creating Razorpay order...");
      const orderPayload = {
        amount: selectedAmount,
        currency: "INR",
        receipt: `receipt_${Date.now()}`,
        notes: {
          service: selectedService?.name,
          customerName: bookingForm.name,
          bookingDate: bookingForm.bookingDate,
        },
      };
      console.log("📦 Order payload:", orderPayload);

      const orderResponse = await fetch(
        "https://srikalikamundeshwari.com/api/razorpay/create-order",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(orderPayload),
        },
      );

      console.log("📥 Order response status:", orderResponse.status);

      if (!orderResponse.ok) {
        const errorText = await orderResponse.text();
        console.error("❌ Order creation failed:", errorText);
        throw new Error(`Server error: ${orderResponse.status} - ${errorText}`);
      }

      const orderData = await orderResponse.json();
      console.log("📋 Order data:", orderData);

      if (!orderData.success) {
        console.error("❌ Order creation failed:", orderData);
        throw new Error(orderData.message || "Failed to create order");
      }

      console.log("✅ Order created:", orderData.orderId);

      // Configure Razorpay options
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: TEMPLE_NAME,
        description: selectedService?.name,
        order_id: orderData.orderId,
        prefill: {
          name: bookingForm.name,
          email: bookingForm.email,
          contact: bookingForm.phone,
        },
        theme: {
          color: "#ea580c",
        },
        handler: async function (response) {
          console.log("✅ Payment successful:", response);
          await createBooking(
            response.razorpay_order_id,
            response.razorpay_payment_id,
            response.razorpay_signature,
          );
        },
        modal: {
          ondismiss: function () {
            console.log("⚠️ Payment modal dismissed");
            setIsSubmitting(false);
            alert("Payment cancelled");
          },
        },
      };

      console.log("🚀 Opening Razorpay checkout...");
      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (error) {
      console.error("❌ Payment error:", error);
      console.error("Error details:", error.message);
      alert(`Payment initiation failed: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  const createBooking = async (orderId, paymentId, signature) => {
    try {
      const bookingData = {
        ...bookingForm,
        paymentAmount: selectedAmount,
        paymentMethod: selectedAmount > 0 ? "razorpay" : "free",
        razorpayOrderId: orderId,
        razorpayPaymentId: paymentId,
        razorpaySignature: signature,
        deviceType: /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
          ? "mobile"
          : "desktop",
      };

      const response = await fetch(
        "https://srikalikamundeshwari.com/api/booking",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bookingData),
        },
      );

      const result = await response.json();

      if (result.success) {
        setSubmitStatus("success");
        alert(
          `✅ Booking Confirmed!\n\nBooking ID: ${result.bookingId}\n${selectedAmount > 0 ? `Amount: ₹${selectedAmount}` : "Free Service"}`,
        );

        setTimeout(() => {
          handleCloseModal();
        }, 3000);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error("Booking error:", error);
      setSubmitStatus("error");
      alert("Booking failed: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPrice = (priceMin, priceMax) => {
    if (priceMin === 0 && priceMax === 0) return "Free";
    if (priceMin === priceMax) return `₹${priceMin}`;
    return `₹${priceMin} - ₹${priceMax}`;
  };

  const getServiceIcon = (serviceName) => {
    const iconMap = {
      "Daily Worship": Landmark,
      "Special Pujas": Flame,
      "Wedding Ceremonies": HeartHandshake,
      "Spiritual Counseling": ScrollText,
      "Meditation Classes": Sparkles,
      "Festival Celebrations": BellRing,
    };
    return iconMap[serviceName] || Landmark;
  };

  if (loading) {
    return (
      <div className="py-16 bg-gradient-to-b from-orange-50 to-white min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Loader2
                className="animate-spin mx-auto mb-4 text-orange-500"
                size={48}
              />
              <p className="text-gray-600 font-medium">
                Loading our sacred services...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-16 bg-gradient-to-b from-orange-50 to-white min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Unable to Load Services
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={fetchServices}
              className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-3 rounded-lg hover:from-orange-600 hover:to-red-600 transform hover:scale-105 transition-all shadow-lg font-semibold"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 bg-gradient-to-b from-orange-50 via-white to-orange-50 min-h-screen animate-fadeIn">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-16 animate-slideUp">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-800">
            Our Sacred Services
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-red-500 mx-auto mb-6"></div>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Experience divine blessings through our traditional temple services
            and spiritual ceremonies
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <div
              key={service.id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border border-orange-100 animate-slideUp"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Service Header */}
              <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 p-8 text-center text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-10 rounded-full -ml-12 -mb-12"></div>
                <div className="mb-3 relative z-10 flex justify-center">
                  {React.createElement(getServiceIcon(service.name), {
                    className: "h-12 w-12 text-white",
                    strokeWidth: 1.8,
                  })}
                </div>
                <h3 className="text-xl font-bold relative z-10">
                  {service.name}
                </h3>
              </div>

              {/* Service Body */}
              <div className="p-6">
                <p className="text-gray-600 mb-6 min-h-[60px] leading-relaxed">
                  {service.description}
                </p>

                {/* Service Details */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center bg-orange-50 p-3 rounded-lg">
                    <Clock
                      className="text-orange-500 mr-3 flex-shrink-0"
                      size={20}
                    />
                    <span className="text-sm text-gray-700 font-medium">
                      {service.availability || service.duration}
                    </span>
                  </div>
                  <div className="flex items-center bg-green-50 p-3 rounded-lg">
                    <span className="text-2xl mr-3">💰</span>
                    <span className="text-sm font-bold text-green-600">
                      {formatPrice(service.price_min, service.price_max)}
                    </span>
                  </div>
                </div>

                {/* Book Button */}
                <button
                  onClick={() => handleBookNow(service)}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-4 rounded-lg hover:from-orange-600 hover:to-red-600 transform hover:scale-105 active:scale-95 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold flex items-center justify-center group"
                >
                  <Calendar
                    className="mr-2 group-hover:animate-bounce"
                    size={20}
                  />
                  {service.price_min > 0 ? "Book & Pay" : "Book Now"}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Why Choose Us Section */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-orange-100 animate-slideUp">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-800">
            Why Choose Our Temple Services?
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-red-500 mx-auto mb-12"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Landmark,
                title: "Experienced Priests",
                desc: "Highly qualified and experienced priests conducting all rituals",
                gradient: "from-orange-50 to-red-50",
              },
              {
                icon: Calendar,
                title: "Easy Booking",
                desc: "Simple online booking system for your convenience",
                gradient: "from-blue-50 to-cyan-50",
              },
              {
                icon: ScrollText,
                title: "Traditional Rituals",
                desc: "Authentic ceremonies following ancient scriptures",
                gradient: "from-purple-50 to-pink-50",
              },
              {
                icon: Sparkles,
                title: "Divine Atmosphere",
                desc: "Peaceful and sacred environment for worship",
                gradient: "from-green-50 to-emerald-50",
              },
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className={`text-center p-6 bg-gradient-to-br ${feature.gradient} rounded-2xl shadow-md hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-100`}
                >
                  <div className="mb-4 flex justify-center">
                    <Icon
                      className="h-12 w-12 text-orange-600"
                      strokeWidth={1.8}
                    />
                  </div>
                  <h4 className="font-bold text-lg mb-3 text-gray-800">
                    {feature.title}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-slideUp">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 flex justify-between items-center rounded-t-2xl">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold">
                  Book Your Service
                </h2>
                <p className="text-orange-100 font-semibold mt-1">
                  {selectedService?.name}
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-all"
              >
                <X size={28} />
              </button>
            </div>

            <div className="p-6 md:p-8">
              <div className="mb-6 p-5 bg-white border border-orange-200 rounded-2xl shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="text-sm text-orange-500 font-semibold">
                      {selectedService?.name}
                    </p>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-800">
                      Book Your Service
                    </h3>
                  </div>
                  <div className="rounded-xl bg-orange-50 px-4 py-3 text-orange-700 font-semibold text-lg shadow-inner">
                    ₹{formatSelectedAmount(selectedAmount)}
                  </div>
                </div>
              </div>

              {/* Success Message */}
              {submitStatus === "success" && (
                <div className="mb-6 p-5 bg-green-50 border-l-4 border-green-500 rounded-lg animate-slideDown">
                  <div className="flex items-center">
                    <CheckCircle
                      className="text-green-500 mr-3 flex-shrink-0"
                      size={28}
                    />
                    <div>
                      <p className="font-bold text-green-800 text-lg">
                        Booking Confirmed! 🎉
                      </p>
                      <p className="text-sm text-green-600 mt-1">
                        A confirmation email has been sent to you
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {submitStatus === "error" && (
                <div className="mb-6 p-5 bg-red-50 border-l-4 border-red-500 rounded-lg animate-slideDown">
                  <div className="flex items-center">
                    <AlertCircle
                      className="text-red-500 mr-3 flex-shrink-0"
                      size={28}
                    />
                    <div>
                      <p className="font-bold text-red-800 text-lg">
                        Booking Failed
                      </p>
                      <p className="text-sm text-red-600 mt-1">
                        Please try again or contact us directly
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Service Details Card */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 p-6 rounded-xl mb-8 border border-orange-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="mr-4 flex h-14 w-14 items-center justify-center rounded-xl bg-white/60 text-orange-700 shadow-inner">
                      {React.createElement(
                        getServiceIcon(selectedService?.name),
                        {
                          className: "h-8 w-8",
                          strokeWidth: 1.8,
                        },
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-xl text-orange-800">
                        {selectedService?.name}
                      </h3>
                      <p className="text-sm text-orange-600 font-semibold mt-1">
                        {formatPrice(
                          selectedService?.price_min,
                          selectedService?.price_max,
                        )}
                      </p>
                    </div>
                  </div>
                  {selectedAmount > 0 && (
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">
                        ₹{selectedAmount}
                      </p>
                      <p className="text-xs text-gray-600">Payment Amount</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Amount Selection */}
              {selectedService &&
                selectedService.price_min !== selectedService.price_max &&
                selectedService.price_min > 0 && (
                  <div className="mb-6">
                    <label className="block text-gray-700 font-semibold mb-3 text-sm">
                      Select Donation Amount
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {getAmountOptions(selectedService).map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleAmountChange(option.value)}
                          className={`p-4 text-sm font-semibold rounded-lg border-2 transition-all ${
                            selectedAmount === option.value
                              ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white border-green-500 shadow-lg scale-105"
                              : "bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:shadow-md"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              {/* Booking Form */}
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2 text-sm">
                      <User className="inline mr-2" size={16} />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={bookingForm.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all outline-none"
                      required
                      placeholder="Enter your full name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2 text-sm">
                      <Mail className="inline mr-2" size={16} />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={bookingForm.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all outline-none"
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2 text-sm">
                      <Phone className="inline mr-2" size={16} />
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={bookingForm.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all outline-none"
                      required
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  {/* Date */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2 text-sm">
                      <Calendar className="inline mr-2" size={16} />
                      Booking Date *
                    </label>
                    <input
                      type="date"
                      name="bookingDate"
                      value={bookingForm.bookingDate}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all outline-none"
                      required
                    />
                  </div>
                </div>

                {/* Time Slots */}
                {bookingForm.bookingDate && (
                  <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                    <label className="block text-gray-700 font-semibold mb-4 text-sm">
                      <Clock className="inline mr-2" size={16} />
                      Select Preferred Time Slot
                    </label>
                    {loadingSlots ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2
                          className="animate-spin mr-3 text-orange-500"
                          size={24}
                        />
                        <span className="text-gray-600">
                          Loading available slots...
                        </span>
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            onClick={() =>
                              setBookingForm((prev) => ({
                                ...prev,
                                bookingTime: slot,
                              }))
                            }
                            className={`p-3 text-sm font-semibold rounded-lg border-2 transition-all ${
                              bookingForm.bookingTime === slot
                                ? "bg-gradient-to-r from-orange-500 to-red-500 text-white border-orange-500 shadow-lg scale-105"
                                : "bg-white text-gray-700 border-gray-300 hover:border-orange-400 hover:shadow-md"
                            }`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <AlertCircle
                          className="mx-auto mb-3 text-gray-400"
                          size={32}
                        />
                        <p className="text-gray-500">
                          No available slots for this date
                        </p>
                        <p className="text-gray-400 text-sm mt-1">
                          Please select another date
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Special Requirements */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2 text-sm">
                    <MessageSquare className="inline mr-2" size={16} />
                    Special Requirements (Optional)
                  </label>
                  <textarea
                    name="specialRequirements"
                    value={bookingForm.specialRequirements}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all outline-none resize-none"
                    placeholder="Any specific requirements or preferences..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 bg-gray-200 text-gray-700 py-4 rounded-lg hover:bg-gray-300 transition-all font-semibold"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={handlePayment}
                    disabled={
                      isSubmitting ||
                      submitStatus === "success" ||
                      !bookingForm.name ||
                      !bookingForm.email ||
                      !bookingForm.phone ||
                      !bookingForm.bookingDate
                    }
                    className={`flex-1 py-4 rounded-lg font-semibold transition-all shadow-lg flex items-center justify-center text-lg ${
                      isSubmitting ||
                      submitStatus === "success" ||
                      !bookingForm.name ||
                      !bookingForm.email ||
                      !bookingForm.phone ||
                      !bookingForm.bookingDate
                        ? "bg-gray-400 cursor-not-allowed text-white"
                        : selectedAmount === 0
                          ? "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 transform hover:scale-105 active:scale-95 text-white"
                          : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 active:scale-95 text-white"
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={22} />
                        Processing...
                      </>
                    ) : submitStatus === "success" ? (
                      <>
                        <CheckCircle className="mr-2" size={22} />
                        Booking Confirmed
                      </>
                    ) : (
                      <>
                        {selectedAmount === 0 ? (
                          <>
                            <CreditCard className="mr-2" size={22} />
                            Pay ₹{formatSelectedAmount(selectedAmount)}
                          </>
                        ) : (
                          <>
                            <CreditCard className="mr-2" size={22} />
                            Pay ₹{selectedAmount}
                          </>
                        )}
                      </>
                    )}
                  </button>
                </div>

                {/* Payment Info */}
                {selectedAmount > 0 && (
                  <div className="text-center mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-800 font-medium">
                      🔒 Secure payment gateway | All major payment methods
                      accepted
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;
